import { useCallback } from 'react';

import { BookIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import PlainFieldset from '@/components/atoms/Fieldset/templates/PlainFieldset';
import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useWebClient } from '@/hooks/useWebClient';
import { cn } from '@/lib/utils';
import { useBillingState } from '@/modules/billing/hooks/useBillingState';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getDocsUrl, MOVEO_CHANNELS_WS_URL, MOVEO_INTEGRATION_ID } from '@/util/constants';

import { FIELDSET_CARD_PADDING } from '../../constants';
import BillingFieldset from './BillingFieldset/BillingFieldset';

import styles from './SidebarFieldset.module.scss';

const getLinkTiles = (slug: string, deskId: string) => [
  {
    title: 'home.follow_guide',
    body: 'home.build_agent',
    buttonText: 'home.view_guide',
    url: getDocsUrl('/docs/ai-agents/quickstart'),
  },
  {
    title: 'home.configure_channels',
    body: 'home.channels_experience',
    buttonText: 'home.browse_integrations',
    url: `/${slug}/environments/${deskId}/integrations`,
  },
  {
    title: 'home.chat',
    body: 'home.view_inbox',
    buttonText: 'home.view_human_chat',
    url: `/${slug}/chats`,
  },
  { title: 'home.contact_support', body: 'home.ai_agent', buttonText: 'common.contact' },
];

const SidebarFieldset = () => {
  const navigate = useNavigate();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);
  const billingState = useBillingState();
  const { t } = useTranslation();
  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );
  const canWriteBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.WRITE)
  );
  const hasBillingPermissions = canReadBilling && canWriteBilling;

  const { initialiseWebchat } = useWebClient({
    host: MOVEO_CHANNELS_WS_URL,
    integrationId: MOVEO_INTEGRATION_ID,
    launcher: { show: false },
  });

  const { desks, listStatus } = useDesks();
  const firstDeskId = listStatus === 'success' && desks[0]?.desk_id;

  const handleClick = useCallback(
    (index: number) => {
      const link = getLinkTiles(slug, deskId ?? firstDeskId)[index]?.url;
      if (index === 0) {
        window.open(link, '_blank');
        return;
      }
      if (index === 3) {
        initialiseWebchat();
        return;
      }
      navigate(link);
    },
    [deskId, firstDeskId, initialiseWebchat, slug, navigate]
  );

  return (
    <div className={cn(styles.container, 'gap-y-4')}>
      {hasBillingPermissions && <BillingFieldset />}
      {getLinkTiles(slug, deskId ?? firstDeskId).map(({ title, body, buttonText }, index) => {
        return (
          <div className={styles.fieldContainer} key={title}>
            <PlainFieldset fullWidth className={FIELDSET_CARD_PADDING}>
              <div className={styles.fieldContent}>
                <span className="text-base leading-6 font-semibold">{t(title)}</span>
                <span className="text-sm leading-5 font-normal">{t(body)}</span>
                <Button variant="outline-neutral" onClick={() => handleClick(index)}>
                  {t(buttonText)}
                </Button>
              </div>
            </PlainFieldset>
          </div>
        );
      })}
      {(billingState?.plan_id !== 'trial' || !canReadBilling) && (
        <PlainFieldset fullWidth className={FIELDSET_CARD_PADDING}>
          <div className={styles.docsContainer}>
            <div className={styles.docsContent}>
              <span>
                <BookIcon size={16} className="text-foreground" />
                <span className="text-base leading-6 font-semibold">{t('docs.docs')}</span>
              </span>
              <span className="text-sm leading-5 font-normal">{t('docs.resources')}</span>
            </div>
            <Button variant="outline-neutral" asChild>
              <a href={getDocsUrl()} target="_blank" rel="noopener noreferrer">
                {t('common.view')}
              </a>
            </Button>
          </div>
        </PlainFieldset>
      )}
    </div>
  );
};

export default SidebarFieldset;
